import { type FC } from 'react'
import { Divider } from 'antd'
import classNames from 'classnames'
import dayjs from 'dayjs'

import { UtilService } from 'common/services/utilService'
import { ALT_CONSTANTS } from 'common/constants/altConstants'

import { CartCollapse } from 'features/Cart/components/CartCollapse/CartCollapse'
import { CartError } from 'features/Cart/components/CartError/CartError'
import type { ITimeSlot } from 'features/Booking/interfaces/ILeaseInfoRoom'
import trashIcon from 'features/Cart/assets/trash.svg'

import styles from './cartShopItem.module.scss'
import collapseStyles from 'features/Cart/components/CartCollapse/cartCollapse.module.scss'

interface IProps {
  slots: ITimeSlot[]
  optionId: number
  handleRemoveSlot: (roomId: number, slotId: number, day?: string) => void
  handleRemoveSlotsForDate?: (date: string) => void
  handleRemoveEntireOption?: () => void
}

export const CartShopSlots: FC<IProps> = ({
  slots,
  optionId,
  handleRemoveSlot,
  handleRemoveSlotsForDate,
  handleRemoveEntireOption,
}) => {
  const slotsByDate = slots.reduce((acc, slot) => {
    const date = slot.date
    if (!acc[date]) {
      acc[date] = []
    }
    acc[date].push(slot)
    return acc
  }, {} as Record<string, typeof slots>)

  return (
    <CartCollapse
      title={`${slots.length} Time Slots Selected`}
      handleRemoveSlot={() => {
        if (handleRemoveEntireOption) {
          handleRemoveEntireOption()
        } else {
          slots.forEach((slot) => handleRemoveSlot(optionId, slot.id))
        }
      }}
      additionalActiveTitle={`${slots.length} slots`}>
      <div>
        {Object.entries(slotsByDate).map(([date, dateSlots]) => (
          <div key={date} className={styles.dateSection}>
            <div className={styles.dateHeader}>
              <span>
                {dayjs(date).format("ddd, MMM DD 'YY")} ({dateSlots.length} slots)
              </span>
              <button
                className={classNames(collapseStyles.parentRemoveButton, 'p-0')}
                onClick={() => {
                  if (handleRemoveSlotsForDate) {
                    handleRemoveSlotsForDate(date)
                  } else {
                    dateSlots.forEach((slot) => handleRemoveSlot(optionId, slot.id))
                  }
                }}
                title='Remove all slots for this day'
                data-cy={`delete-slots-${date}`}>
                <img
                  src={trashIcon}
                  alt={ALT_CONSTANTS.ACTION_ICON}
                  className={collapseStyles.parentRoomRemoveIcon}
                />
              </button>
            </div>
            {dateSlots.map((slot) => (
              <div key={slot.id} className={styles.divider}>
                <div key={slot.id} className={classNames(styles.parent, styles.slotItem)}>
                  <div>{slot.title}</div>
                  <span>{UtilService.numberToDollar(slot.price)}</span>
                </div>
                <CartError error={slot?.error_message} className={styles.slotItem} />
              </div>
            ))}

            <div className={`${styles.parent} ${styles.dayTotal}`}>
              <span>Day Total</span>
              <span>
                {UtilService.numberToDollar(dateSlots.reduce((sum, s) => sum + s.price, 0))}
              </span>
            </div>
          </div>
        ))}
        <Divider className={styles.divider} />
        <div className={classNames(styles.parent, styles.grandTotal)}>
          <span>Grand Total</span>
          <span>{UtilService.numberToDollar(slots.reduce((sum, s) => sum + s.price, 0))}</span>
        </div>
      </div>
    </CartCollapse>
  )
}
